// AIDEV-NOTE: Fixed set of allowed relationship types with their inverses
export const RELATIONSHIP_TYPES = {
    PROTECTED: "protected",
    PROTECTS: "protects",
    POLARIZED_WITH: "polarized-with",
    ALLIED_WITH: "allied-with",
    CONTROLS_ACCESS_TO: "controls-access-to",
    ACCESS_MEDIATED_BY: "access-mediated-by",
    SUPPORTS: "supports",
    SUPPORTED_BY: "supported-by",
} as const;

export type RelationshipType =
    (typeof RELATIONSHIP_TYPES)[keyof typeof RELATIONSHIP_TYPES];

export const RELATIONSHIP_TYPE_VALUES: readonly RelationshipType[] =
    Object.values(RELATIONSHIP_TYPES);

// AIDEV-NOTE: Map each type to its inverse for validation and consistency
export const RELATIONSHIP_INVERSE_MAP: Record<
    RelationshipType,
    RelationshipType
> = {
    [RELATIONSHIP_TYPES.PROTECTED]: RELATIONSHIP_TYPES.PROTECTS,
    [RELATIONSHIP_TYPES.PROTECTS]: RELATIONSHIP_TYPES.PROTECTED,
    [RELATIONSHIP_TYPES.POLARIZED_WITH]: RELATIONSHIP_TYPES.POLARIZED_WITH,
    [RELATIONSHIP_TYPES.ALLIED_WITH]: RELATIONSHIP_TYPES.ALLIED_WITH,
    [RELATIONSHIP_TYPES.CONTROLS_ACCESS_TO]:
        RELATIONSHIP_TYPES.ACCESS_MEDIATED_BY,
    [RELATIONSHIP_TYPES.ACCESS_MEDIATED_BY]:
        RELATIONSHIP_TYPES.CONTROLS_ACCESS_TO,
    [RELATIONSHIP_TYPES.SUPPORTS]: RELATIONSHIP_TYPES.SUPPORTED_BY,
    [RELATIONSHIP_TYPES.SUPPORTED_BY]: RELATIONSHIP_TYPES.SUPPORTS,
};

// Helper to validate if a string is a valid relationship type
export function isValidRelationshipType(
    type: string,
): type is RelationshipType {
    return Object.values(RELATIONSHIP_TYPES).includes(type as RelationshipType);
}

// Helper to get the inverse of a relationship type
export function getInverseRelationshipType(
    type: RelationshipType,
): RelationshipType {
    return RELATIONSHIP_INVERSE_MAP[type];
}

// AIDEV-NOTE: Single choke point for LLM-emitted relationship types. The v2
// emit_session schema and the v1 text path both let the model free-type
// `type`, and inverseType used to fall back to the non-existent "related_to".
// Returns null for unknown types so callers drop the edge instead of storing it.
// The inverse is always derived from `type`; a model-sent inverseType is never trusted.
export function normalizeRelationshipTypes({
    type,
}: {
    type: string;
}): { type: RelationshipType; inverseType: RelationshipType } | null {
    if (!isValidRelationshipType(type)) return null;
    return { type, inverseType: getInverseRelationshipType(type) };
}
